import type {
  OverageAcceptanceOfferResponse,
  SpendingControls,
} from "@/types_api/backend/transport/overage_acceptance_offer_response"
import type { OverageAcceptanceRequest } from "@/types_api/backend/transport/overage_acceptance_request"

import type {
  PaidOverageReview,
  PaidOverageReviewPort,
} from "../hooks/usePaidOverageReview"

type Offer = NonNullable<OverageAcceptanceOfferResponse["offer"]>
type Intent = Parameters<PaidOverageReviewPort["decide"]>[0]

interface ReviewSnapshot {
  version: 1
  scopeKey: string
  actorId: string
  response: OverageAcceptanceOfferResponse
}

const OFFER_EXPIRY_MARGIN_MS = 30_000
const UUID4 =
  /^[\da-f]{8}-[\da-f]{4}-4[\da-f]{3}-[89ab][\da-f]{3}-[\da-f]{12}$/i
const DECIMAL_PRICE = /^-?\d+(?:\.\d+)?(?:[Ee][+-]?\d+)?$/

class InvalidPaidOverageIntent extends Error {}

export function paidOverageCanonicalJson(value: unknown): string {
  function ordered(child: unknown): unknown {
    if (Array.isArray(child)) {
      return child.map(ordered)
    }
    if (child !== null && typeof child === "object") {
      return Object.fromEntries(
        Object.entries(child)
          .sort(([left], [right]) => (left < right ? -1 : left > right ? 1 : 0))
          .map(([key, entry]) => [key, ordered(entry)]),
      )
    }
    return child
  }
  return JSON.stringify(ordered(value)).replace(
    /[\u007f-\uffff]/g,
    (character) =>
      `\\u${character.charCodeAt(0).toString(16).padStart(4, "0")}`,
  )
}

/** Mirrors the backend's sorted ASCII JSON fingerprint without numeric price conversion. */
export async function verifyPaidOverageFingerprint(
  response: OverageAcceptanceOfferResponse,
) {
  if (response.offer === null || response.offer_fingerprint === null) {
    throw new Error("The approved offer fingerprint is required.")
  }
  const bytes = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(paidOverageCanonicalJson(response.offer)),
  )
  const fingerprint = Array.from(new Uint8Array(bytes), (value) =>
    value.toString(16).padStart(2, "0"),
  ).join("")
  if (fingerprint !== response.offer_fingerprint) {
    throw new Error(
      "The displayed financial terms do not match the approved offer.",
    )
  }
}

function inactive(value: unknown) {
  return value === undefined || value === false
}

export function verifyPaidOverageScope(
  organizationId: string,
  response: Pick<
    OverageAcceptanceOfferResponse,
    "organization_id" | "charging_active" | "enforcement_active"
  >,
) {
  identifier(organizationId)
  if (
    response.organization_id !== organizationId ||
    !inactive(response.charging_active) ||
    !inactive(response.enforcement_active)
  ) {
    throw new Error("The inactive organization scope could not be verified.")
  }
}

function identifier(value: string) {
  if (typeof value !== "string" || value.length < 1 || value.length > 512) {
    throw new Error("The financial scope could not be verified.")
  }
}

export function paidOverageTimestamp(value: string) {
  const parsed = Date.parse(value)
  if (
    typeof value !== "string" ||
    !/(?:Z|[+-]\d{2}:\d{2})$/.test(value) ||
    !Number.isFinite(parsed)
  ) {
    throw new Error("The approved time interval could not be verified.")
  }
  return parsed
}

function minorAmount(value: number) {
  return Number.isSafeInteger(value) && value >= 0
}

export function verifyPaidOverageControls(
  controls: SpendingControls,
  organizationId: string,
) {
  identifier(controls.revision_id)
  if (
    controls.organization_id !== organizationId ||
    !/^[A-Z]{3}$/.test(controls.currency) ||
    typeof controls.overages_enabled !== "boolean" ||
    !["unset", "limited", "unlimited"].includes(controls.limit_state) ||
    (controls.limit_state === "limited"
      ? !minorAmount(controls.limit_minor ?? -1)
      : controls.limit_minor !== null)
  ) {
    throw new Error("The organization settings could not be verified.")
  }
}

function verifyOffer(offer: Offer, organizationId: string) {
  identifier(offer.revision_id)
  identifier(offer.terms.policy.revision_id)
  identifier(offer.terms.policy.terms_reference)
  identifier(offer.disclosure.version)
  identifier(offer.disclosure.reference)
  identifier(offer.disclosure.collection_policy_reference)
  identifier(offer.payment_evidence_reference)
  const start = paidOverageTimestamp(offer.effective_start)
  const end = paidOverageTimestamp(offer.effective_end)
  if (
    offer.organization_id !== organizationId ||
    offer.terms.policy.organization_id !== organizationId ||
    typeof offer.terms.policy.eligible !== "boolean" ||
    !offer.terms.policy.eligible ||
    !/^[A-Z]{3}$/.test(offer.terms.currency) ||
    !["half_up", "ceiling"].includes(offer.terms.rounding) ||
    paidOverageTimestamp(offer.terms.policy.effective_start) > start ||
    paidOverageTimestamp(offer.terms.policy.effective_end) < end ||
    start >= end ||
    !["ready", "setup_required", "authentication_required"].includes(
      offer.payment_state,
    ) ||
    typeof offer.disclosure.text !== "string" ||
    offer.disclosure.text.length < 1 ||
    offer.disclosure.text.length > 8000 ||
    typeof offer.disclosure.collection_timing !== "string" ||
    offer.disclosure.collection_timing.length < 1 ||
    offer.disclosure.collection_timing.length > 2000 ||
    !Array.isArray(offer.allowed_limit_states) ||
    offer.allowed_limit_states.length < 1 ||
    offer.allowed_limit_states.length > 2 ||
    new Set(offer.allowed_limit_states).size !==
      offer.allowed_limit_states.length ||
    offer.allowed_limit_states.some(
      (mode) => mode !== "limited" && mode !== "unlimited",
    ) ||
    (offer.allowed_limit_states.includes("limited")
      ? !minorAmount(offer.maximum_limit_minor ?? -1)
      : offer.maximum_limit_minor !== null) ||
    !["subscription_period", "standing"].includes(
      offer.consent_scope ?? "subscription_period",
    ) ||
    (offer.consent_scope === "standing"
      ? !["month", "year"].includes(offer.budget_reset_interval ?? "")
      : offer.budget_reset_interval != null) ||
    !Array.isArray(offer.terms.meters) ||
    offer.terms.meters.length === 0
  ) {
    throw new Error("The approved financial offer could not be verified.")
  }
  for (const meter of offer.terms.meters) {
    identifier(meter.meter_id)
    if (
      !["simulation", "conversation", "metric"].includes(meter.source_type) ||
      !["minutes", "count"].includes(meter.dimension) ||
      !minorAmount(meter.included_quantity) ||
      typeof meter.unit_price_minor !== "string" ||
      !DECIMAL_PRICE.test(meter.unit_price_minor) ||
      !Number.isFinite(Number(meter.unit_price_minor)) ||
      Number(meter.unit_price_minor) < 0 ||
      (meter.simulator_types != null &&
        (!Array.isArray(meter.simulator_types) ||
          meter.simulator_types.some((value) => typeof value !== "string")))
    ) {
      throw new Error("The approved price could not be verified.")
    }
  }
  return { start, end }
}

export function createPaidOverageReview(
  scopeKey: string,
  actorId: string,
  organizationId: string,
  response: OverageAcceptanceOfferResponse,
  now = Date.now(),
): PaidOverageReview | null {
  if (typeof scopeKey !== "string" || scopeKey.length === 0) {
    throw new Error("The review scope is required.")
  }
  identifier(actorId)
  verifyPaidOverageScope(organizationId, response)
  if (response.controls !== null) {
    verifyPaidOverageControls(response.controls, organizationId)
  }
  if (response.availability !== "available") {
    if (
      !["not_configured", "not_eligible", "not_launched"].includes(
        response.availability,
      ) ||
      response.offer !== null ||
      response.offer_fingerprint !== null
    ) {
      throw new Error("Offer availability could not be verified.")
    }
    return null
  }
  const offer = response.offer
  if (
    offer === null ||
    response.controls === null ||
    !/^[\da-f]{64}$/.test(response.offer_fingerprint ?? "")
  ) {
    throw new Error("The complete approved offer is required.")
  }
  const { start, end } = verifyOffer(offer, organizationId)
  if (
    response.controls.currency !== offer.terms.currency ||
    !Number.isFinite(now) ||
    now < start ||
    now + OFFER_EXPIRY_MARGIN_MS >= end
  ) {
    throw new Error("Reload an offer within its approved decision interval.")
  }
  const snapshot: ReviewSnapshot = { version: 1, scopeKey, actorId, response }
  const standing = offer.consent_scope === "standing"
  const renewal =
    offer.budget_reset_interval === "month"
      ? "Consumed spending resets with observed monthly subscription periods; unused budget does not carry over."
      : "The annual spending window does not reset with monthly included-usage allowances."
  return {
    scopeKey,
    reviewKey: JSON.stringify(snapshot),
    disclosureVersion: offer.disclosure.version,
    disclosureText: offer.disclosure.text,
    priceLines: [
      ...offer.terms.meters.map(
        (meter) =>
          `${meter.meter_id} (${meter.source_type}, ${meter.dimension}): ${meter.included_quantity} included; ${meter.unit_price_minor} ${offer.terms.currency} minor units per measured unit${meter.simulator_types == null ? "" : `; simulator types: ${meter.simulator_types.join(", ")}`}.`,
      ),
      `Pricing terms: ${offer.terms.policy.terms_reference}. Rounding policy: ${offer.terms.rounding}.`,
    ],
    collectionTiming: offer.disclosure.collection_timing,
    validityLabel: `Offer decision deadline: ${offer.effective_end}. The budget is shared across the listed usage categories. ${standing ? `Approval and the saved cap persist across renewals under unchanged approved terms until controls change. ${renewal}` : "Approval applies only to the offered subscription period."} Subscription entitlement ending stops paid-overage authority. Subscription or terms changes may require a fresh review.`,
    currency: offer.terms.currency,
    limitedMaximumMinor: offer.maximum_limit_minor,
    unlimitedOffered: offer.allowed_limit_states.includes("unlimited"),
    paymentStatus:
      offer.payment_state === "ready"
        ? "ready"
        : offer.payment_state === "setup_required"
          ? "setup"
          : "authentication",
  }
}

export function readPaidOverageReviewKey(
  key: string,
  scopeKey: string,
  actorId: string,
) {
  const snapshot = JSON.parse(key) as ReviewSnapshot
  if (
    snapshot?.version !== 1 ||
    snapshot.scopeKey !== scopeKey ||
    snapshot.actorId !== actorId ||
    snapshot.response.availability !== "available" ||
    snapshot.response.offer === null ||
    snapshot.response.controls === null
  ) {
    throw new InvalidPaidOverageIntent("The original review is unavailable.")
  }
  const checked = createPaidOverageReview(
    scopeKey,
    actorId,
    snapshot.response.organization_id,
    snapshot.response,
    paidOverageTimestamp(snapshot.response.offer.effective_start),
  )
  if (checked?.reviewKey !== key) {
    throw new InvalidPaidOverageIntent("The original review changed.")
  }
  return snapshot
}

export function paidOverageRequest(
  snapshot: ReturnType<typeof readPaidOverageReviewKey>,
  intent: Intent,
): OverageAcceptanceRequest {
  const offer = snapshot.response.offer
  const controls = snapshot.response.controls
  const budget = intent.budget
  if (
    offer === null ||
    controls === null ||
    intent.reviewKey !== JSON.stringify(snapshot) ||
    !UUID4.test(intent.attemptId) ||
    !["signup", "quick_enablement"].includes(intent.surface) ||
    !["accept", "decline"].includes(intent.decision) ||
    (intent.recoveryAttemptId !== null &&
      (!UUID4.test(intent.recoveryAttemptId) ||
        intent.recoveryAttemptId === intent.attemptId)) ||
    (intent.decision === "decline"
      ? budget !== null
      : budget === null ||
        !offer.allowed_limit_states.includes(budget.mode) ||
        (budget.mode === "limited" &&
          (!minorAmount(budget.minor) ||
            offer.maximum_limit_minor === null ||
            budget.minor > offer.maximum_limit_minor)))
  ) {
    throw new InvalidPaidOverageIntent(
      "The complete original choice is required.",
    )
  }
  return {
    request_id: intent.attemptId,
    offer_revision_id: offer.revision_id,
    offer_fingerprint: snapshot.response.offer_fingerprint ?? "",
    expected_controls_revision_id: controls.revision_id,
    surface: intent.surface,
    decision: intent.decision,
    limit_state: budget?.mode ?? "unset",
    limit_minor: budget?.mode === "limited" ? budget.minor : null,
    recovery_request_id: intent.recoveryAttemptId,
  }
}
